import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { type PaymentButtonData, type PaymentButtonHandlers } from '@ctct/application-layer';
import { ActiveConsentPaymentTerms, PayButton } from '@ctct/presentation-layer';
import type { PaymentButtonHooks } from '@ctct/state-client-layer';
import { useIsSocialStarter } from '@ctct/state-server-layer';

import { PaymentTermsContent } from './components/PaymentTermsContent';
import type { PaymentButtonWidgetProps } from './usePaymentButtonWidget';

interface Props {
  data: PaymentButtonData;
  handlers: PaymentButtonHandlers;
  hooks: PaymentButtonHooks;
  externalHooks: PaymentButtonWidgetProps['externalHooks'];
  version: 'standard' | 'split-terms-text';
  isCustomer?: boolean;
}

export const PaymentButtonWidgetContent = ({ data, handlers, hooks, externalHooks, version, isCustomer }: Props) => {
  const disabled = hooks.useDisabled() || data.isSingleBillChildAccount();
  const priceLabel = hooks.usePriceLabel();
  const prepay = externalHooks.usePrepay();
  const hasAcceptedTerms = hooks.useHasAcceptedTerms();
  const termsErrorMessage = hooks.useTermsErrorMessage();
  const isPadPayment = hooks.useIsPadPaymentMethod();
  const billingCountryCode = hooks.useBillingCountryCode();

  const requiresActiveConsent = data.requiresActiveTermsConsent({ isPadPayment });
  const { isSocialStarter } = useIsSocialStarter();

  const { t } = useTranslation();

  const contentKeys = data.getPaymentTermsContentKeys(prepay, isSocialStarter ?? false, billingCountryCode, isCustomer);

  const isSplitVersion = version === 'split-terms-text';

  let paymentTerms = (
    <PaymentTermsContent
      contentKeys={contentKeys}
      onTierPricingClick={handlers.onShowEmailPricingTiersClick}
      showAutoRenewalTerms
      showAcknowledgement
      showChargesTerms={!isSplitVersion}
    />
  );

  const bottomTerms = (
    <PaymentTermsContent
      contentKeys={contentKeys}
      onTierPricingClick={handlers.onShowEmailPricingTiersClick}
      showAutoRenewalTerms={false}
      showAcknowledgement={false}
      showChargesTerms
    />
  );

  if (requiresActiveConsent) {
    paymentTerms = (
      <ActiveConsentPaymentTerms
        hasConsented={hasAcceptedTerms}
        onChange={handlers.setHasAcceptedTermsAndConditions}
        errorMessage={termsErrorMessage}
        id="payment-terms-checkbox"
        a11yTitle={t(contentKeys.termsCheckboxA11yTitleKey)}
      >
        {paymentTerms}
      </ActiveConsentPaymentTerms>
    );
  }

  return (
    <div className={classNames('payflow-presentation pay-button-container', `version-${version}`)}>
      <PayButton
        disabled={disabled}
        onClick={handlers.onPayButtonClick}
        buttonLabel={data.getButtonLabel(priceLabel)}
        terms={paymentTerms}
        termsBottom={isSplitVersion ? bottomTerms : undefined}
        variant={data.isLoading() ? 'loading' : undefined}
      />
    </div>
  );
};
